/**
 * @file Detector matcher — pure functions over a single command event.
 * @module @labcraft/course-kit/runtime/scanner
 *
 * Replaces the old pty-byte scanner (#629). Completion is deterministic
 * over the `{cmd, exit, cwd}` command-event stream produced by the
 * OSC 133 pipeline (#156 Phase 1b): the host parses markers → publishes
 * `GateSub` events → lab persists + forwards a `command` frame →
 * `<ChapterProvider>` feeds each event here.
 *
 * `shell` / `exit` evaluate here. `manual` never matches the event
 * stream — it's completed by an explicit reader action (the "I did it"
 * button dispatches `objective.completed`). `state` never matches here
 * either — it's evaluated asynchronously by the probe scheduler
 * (`StateGoalScheduler`, #1149) via the out-of-band guest probe RPC
 * (#1147); a satisfied probe resolves against the same command window
 * through {@link scanObjectiveCheck}.
 */

import type {
  Detector,
  Objective,
  ObjectiveCommand,
  WrongTurn,
} from '../types.ts'

/**
 * One command observed on the learner's pty, normalized from the lab
 * `command` frame (which carries the OSC 133 parser's payload). Lean
 * by design (#1144): advancement needs only `{cmd, exit, cwd}` — the
 * terminal already shows the learner their output, and the mentor's
 * full-output path rides a separate lab→mentor relay, never the
 * browser.
 *
 * @property cmd - the command line the learner ran
 * @property exit - its exit code
 * @property cwd - the working directory at command time
 */
export interface CommandEvent {
  cmd: string
  exit: number
  cwd: string
}

/**
 * Whether `detector` matches `event`. `shell` honours the
 * string-prefix / RegExp + optional cwd + exit (default 0) rules from
 * the Build Spec; `exit` matches the code; everything else is `false`
 * for the event stream (`manual` is action-driven; `state` is
 * probe-driven via the asynchronous scheduler, #1149).
 *
 * @param detector - the condition to test
 * @param event - the command event
 * @returns whether the detector is satisfied by this event
 */
export function matchDetector(
  detector: Detector,
  event: CommandEvent,
): boolean {
  switch (detector.via) {
    case 'shell': {
      if ((detector.exit ?? 0) !== event.exit) return false
      const cmdOk =
        typeof detector.match === 'string'
          ? event.cmd === detector.match ||
            event.cmd.startsWith(`${detector.match} `)
          : detector.match.test(event.cmd)
      if (!cmdOk) return false
      if (detector.cwd !== undefined) {
        const cwdOk =
          typeof detector.cwd === 'string'
            ? event.cwd === detector.cwd
            : detector.cwd.test(event.cwd)
        if (!cwdOk) return false
      }
      return true
    }
    case 'exit':
      return event.exit === detector.code
    case 'manual':
    case 'state':
      return false
  }
}

/**
 * The scan window at `commandIndex`: every not-yet-matched command a
 * single match may resolve — unmatched **optional** commands from the
 * pointer onward, up to and including the next **required** one. The
 * scanner may skip over unmatched optionals, never past an unmatched
 * required command; both the event path ({@link scanObjective}) and
 * the probe path ({@link scanObjectiveCheck}) resolve against this
 * same window so the two advancement channels can't disagree.
 *
 * @param objective - the active objective
 * @param commandIndex - index of the first not-yet-matched command
 * @returns the candidate commands, each with its index
 */
export function scanWindow(
  objective: Objective,
  commandIndex: number,
): { command: ObjectiveCommand; index: number }[] {
  const window: { command: ObjectiveCommand; index: number }[] = []
  for (let i = commandIndex; i < objective.commands.length; i++) {
    const command = objective.commands[i]!
    window.push({ command, index: i })
    if (!command.optional) break
  }
  return window
}

/** The outcome of testing one command event against the active objective. */
export interface ObjectiveScan {
  /**
   * Index into `objective.commands` of the command this event matched,
   * or `null` when nothing matched. May be past `commandIndex` when the
   * event skipped over unmatched **optional** commands to a later one.
   */
  matchedIndex: number | null
  /**
   * True when the match leaves no required command outstanding — the
   * objective is done.
   */
  satisfied: boolean
  /**
   * The wrong turn this event triggered, if any. Only set when the
   * event did **not** match any candidate command (a satisfying command
   * is never also a wrong turn).
   */
  wrongTurn: WrongTurn | null
}

/**
 * Whether a match at `matchedIndex` leaves no required command
 * outstanding. An objective's last command is required
 * (`lintChapter`-enforced), so in practice: the final command matched.
 */
function tailSatisfied(objective: Objective, matchedIndex: number): boolean {
  return objective.commands
    .slice(matchedIndex + 1)
    .every(c => c.optional === true)
}

/**
 * Test one command event against the active objective's command
 * sequence, starting at `commandIndex` (the first not-yet-matched
 * command).
 *
 * Candidates are the {@link scanWindow} at `commandIndex` — the event
 * may skip over unmatched optional commands, but never past an
 * unmatched required one. On a match the objective is satisfied iff
 * every command after the matched one is optional. On no match, the
 * first matching `wrongTurn` (if any) is reported; the mentor reads it
 * and it never gates completion.
 *
 * @param objective - the active objective
 * @param commandIndex - index of the first not-yet-matched command
 * @param event - the command event
 * @returns the scan outcome
 */
export function scanObjective(
  objective: Objective,
  commandIndex: number,
  event: CommandEvent,
): ObjectiveScan {
  let matchedIndex: number | null = null
  for (const { command, index } of scanWindow(objective, commandIndex)) {
    if (command.satisfiedBy.some(d => matchDetector(d, event))) {
      matchedIndex = index
      break
    }
  }

  if (matchedIndex === null) {
    const wrongTurn =
      objective.wrongTurns.find(w => matchDetector(w.detect, event)) ?? null
    return { matchedIndex, satisfied: false, wrongTurn }
  }

  return {
    matchedIndex,
    satisfied: tailSatisfied(objective, matchedIndex),
    wrongTurn: null,
  }
}

/**
 * Resolve a satisfied `{via:'state'}` probe against the active
 * objective's command sequence — the probe-path analogue of
 * {@link scanObjective} (#1149). The satisfied `check` resolves the
 * first {@link scanWindow} candidate whose `satisfiedBy` carries it;
 * wrong turns never fire on this path (a probe is not a command the
 * learner ran).
 *
 * @param objective - the active objective
 * @param commandIndex - index of the first not-yet-matched command
 * @param check - the state check a probe just reported satisfied
 * @returns the scan outcome (`wrongTurn` always `null`)
 */
export function scanObjectiveCheck(
  objective: Objective,
  commandIndex: number,
  check: string,
): ObjectiveScan {
  let matchedIndex: number | null = null
  for (const { command, index } of scanWindow(objective, commandIndex)) {
    if (command.satisfiedBy.some(d => d.via === 'state' && d.check === check)) {
      matchedIndex = index
      break
    }
  }

  if (matchedIndex === null) {
    return { matchedIndex, satisfied: false, wrongTurn: null }
  }
  return {
    matchedIndex,
    satisfied: tailSatisfied(objective, matchedIndex),
    wrongTurn: null,
  }
}
